import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ChevronRightIcon from '../common/ChevronRightIcon';
import houseIcon from '../common/icons/house.png';
import coinIcon from '../common/icons/coin.png';
import ChatUserAvatar from '../MessagesModal/ChatUserAvatar';
import ProfileHeader from './ProfileHeader';
import PriceEstimations, { EstimationListItem } from '../../pages/PriceEstimations';
import NotificationSettings from '../../pages/NotificationSettings';
import MyListings from '../../pages/MyListings';
import { AreaInterest } from '../AppContent/NewLandingPage/CouldBuyFlow/areaInterestApi';
import { useProfileNavigation, ProfileSubview } from '../../contexts/ProfileNavigationContext';
import { getCookie } from '../utils/utils';
import { apiRequest } from '../utils/api';
import { useIdentityVerification } from '../../hooks/useIdentityVerification';
import './ProfileView.scss';

const FINNISH_PHONE_REGEX = /^\+358\d{6,12}$/;

type PhoneEditMode = 'idle' | 'input' | 'verify';

function maskPhone(phone: string): string {
  if (!phone || phone.length < 8) return phone || '';
  const last4 = phone.slice(-4);
  const prefix = phone.slice(0, 4);
  return `${prefix} ** ***${last4}`;
}

function mapPhoneError(err: any, t: (key: string) => string): string {
  const code = err?.error;
  switch (code) {
    case 'invalid_phone_number':
      return t('profile.invalidPhone');
    case 'phone_number_already_in_use':
      return t('profile.phoneAlreadyInUse');
    case 'phone_already_verified':
      return t('profile.phoneAlreadyVerified');
    case 'rate_limit_exceeded':
      return t('profile.rateLimitExceeded');
    case 'sms_send_failed':
      return t('profile.smsSendFailed');
    case 'invalid_verification_code':
      return t('profile.invalidCode');
    case 'verification_code_expired':
      return t('profile.codeExpired');
    default:
      return err?.message || t('profile.genericError');
  }
}

type ProfileViewProps = {
  userEmail: string | null;
  isLoggedIn: boolean;
  setIsLoggedIn: (isLoggedIn: boolean) => void;
  onClose: () => void;
  onLogoutStart?: () => void;
  onLogoutFailed?: () => void;
  onLogoutSuccess?: () => void;
  headerVariant?: 'default' | 'newLandingPage';
  /** Called when the buyer taps Edit on an area interest in the buy list. The
   *  caller should open CouldBuyFlow pre-filled with the given form state.
   *  areaInterestId is passed so the flow can PUT instead of POST. */
  onEditAreaInterest?: (interest: AreaInterest) => void;
};

/**
 * Graphic for the Price estimations box — mirrors the building view's
 * empty-prompt cluster (house front-left, two coins stacked behind, Figma
 * 2384:38727) so the profile reuses the same sell+buy iconography (JAA-308).
 */
const PriceEstimationIcons = () => (
  <div className="profile-view__price-estimation-icons" aria-hidden="true">
    <img
      className="profile-view__estimation-coin profile-view__estimation-coin--front"
      src={coinIcon}
      alt=""
    />
    <img className="profile-view__estimation-house" src={houseIcon} alt="" />
  </div>
);

/**
 * The signed-in user's profile sheet (mobile) / card (desktop): account summary,
 * listings and estimations entry points, the SMS phone number section and the
 * settings menu. Subviews open over it via openSubview.
 */
const ProfileView: React.FC<ProfileViewProps> = ({
  userEmail,
  isLoggedIn,
  setIsLoggedIn,
  onClose,
  onLogoutStart,
  onLogoutFailed,
  onLogoutSuccess,
  headerVariant = 'default',
  onEditAreaInterest,
}) => {
  const { t, i18n } = useTranslation();
  const { subview, estimationIdFromUrl, openSubview, closeSubview, openEstimationDetail, closeEstimationDetail } = useProfileNavigation();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const identityVerification = useIdentityVerification(isLoggedIn);
  const isVerified = identityVerification.status === 'verified';
  const isMountedRef = useRef(true);
  const [priceEstimations, setPriceEstimations] = useState<EstimationListItem[]>([]);

  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [phoneEditMode, setPhoneEditMode] = useState<PhoneEditMode>('idle');
  const [phoneInput, setPhoneInput] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [phoneSuccess, setPhoneSuccess] = useState<string | null>(null);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneStatusLoading, setPhoneStatusLoading] = useState(true);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchPhoneStatus = useCallback(async () => {
    if (!isLoggedIn) return;
    try {
      setPhoneStatusLoading(true);
      const data = await apiRequest('/sms-notifications/status', { method: 'GET' });
      if (isMountedRef.current) {
        setPhoneNumber(data.phone_number || null);
        setPhoneVerified(data.phone_verified || false);
      }
    } catch {
      // Silently handle - phone section will show "no phone" state
    } finally {
      if (isMountedRef.current) setPhoneStatusLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    fetchPhoneStatus();
  }, [fetchPhoneStatus]);

  const smsLanguage = i18n.language?.startsWith('fi') ? 'fi' : 'en';

  const cleanedPhoneInput = '+358' + phoneInput.replace(/\s/g, '');
  const phoneUnchanged = phoneVerified && !!phoneNumber && cleanedPhoneInput === phoneNumber;

  const handleAddPhone = useCallback(async () => {
    const cleaned = '+358' + phoneInput.replace(/\s/g, '');
    if (!FINNISH_PHONE_REGEX.test(cleaned)) {
      setPhoneError(t('profile.invalidPhone'));
      return;
    }
    setPhoneError(null);
    setPhoneSuccess(null);
    setPhoneLoading(true);
    try {
      await apiRequest('/phone/add', {
        method: 'POST',
        body: { phone_number: cleaned, language: smsLanguage },
      });
      if (isMountedRef.current) {
        setPhoneNumber(cleaned);
        setPhoneVerified(false);
        setPhoneEditMode('verify');
        setPhoneSuccess(t('profile.codeSent', { phone: cleaned }));
      }
    } catch (err: any) {
      if (isMountedRef.current) setPhoneError(mapPhoneError(err, t));
    } finally {
      if (isMountedRef.current) setPhoneLoading(false);
    }
  }, [phoneInput, smsLanguage, t]);

  const handleVerifyCode = useCallback(async () => {
    const code = codeInput.trim();
    if (!code || code.length !== 6) {
      setPhoneError(t('profile.invalidCode'));
      return;
    }
    setPhoneError(null);
    setPhoneSuccess(null);
    setPhoneLoading(true);
    try {
      await apiRequest('/phone/verify', {
        method: 'POST',
        body: { verification_code: code },
      });
      if (isMountedRef.current) {
        setPhoneVerified(true);
        setPhoneEditMode('idle');
        setCodeInput('');
        setPhoneSuccess(t('profile.phoneVerified'));
        await fetchPhoneStatus();
      }
    } catch (err: any) {
      if (isMountedRef.current) setPhoneError(mapPhoneError(err, t));
    } finally {
      if (isMountedRef.current) setPhoneLoading(false);
    }
  }, [codeInput, t, fetchPhoneStatus]);

  const handleResendCode = useCallback(async () => {
    setPhoneError(null);
    setPhoneSuccess(null);
    setPhoneLoading(true);
    try {
      await apiRequest('/phone/resend-code', {
        method: 'POST',
        body: { language: smsLanguage },
      });
      if (isMountedRef.current) setPhoneSuccess(t('profile.codeResent'));
    } catch (err: any) {
      if (isMountedRef.current) setPhoneError(mapPhoneError(err, t));
    } finally {
      if (isMountedRef.current) setPhoneLoading(false);
    }
  }, [smsLanguage, t]);

  const handleRemovePhone = useCallback(async () => {
    setPhoneError(null);
    setPhoneSuccess(null);
    setPhoneLoading(true);
    try {
      await apiRequest('/phone/remove', { method: 'DELETE' });
      if (isMountedRef.current) {
        setPhoneNumber(null);
        setPhoneVerified(false);
        setPhoneEditMode('idle');
        setPhoneInput('');
        setCodeInput('');
        setShowRemoveConfirm(false);
      }
    } catch (err: any) {
      if (isMountedRef.current) setPhoneError(mapPhoneError(err, t));
    } finally {
      if (isMountedRef.current) setPhoneLoading(false);
    }
  }, [t]);

  const startEditPhone = useCallback(() => {
    setPhoneEditMode('input');
    setPhoneInput((phoneNumber || '').replace(/^\+358/, ''));
    setPhoneError(null);
    setPhoneSuccess(null);
    setCodeInput('');
    setShowRemoveConfirm(false);
  }, [phoneNumber]);

  const cancelEditPhone = useCallback(() => {
    setPhoneEditMode('idle');
    setPhoneInput('');
    setCodeInput('');
    setPhoneError(null);
    setPhoneSuccess(null);
    setShowRemoveConfirm(false);
  }, []);

  const handleLogout = async () => {
    if (isMountedRef.current) setIsLoggingOut(true);
    onLogoutStart?.();
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
      });

      if (response.ok) {
        setIsLoggedIn(false);
        onLogoutSuccess?.();
        if (!onLogoutSuccess) {
          onClose();
          window.location.assign('/');
        }
      } else {
        console.error('Logout failed');
        onLogoutFailed?.();
        if (!onLogoutSuccess) {
          window.location.reload();
        }
      }
    } catch (error) {
      console.error('Error:', error);
      onLogoutFailed?.();
      if (!onLogoutSuccess) {
        window.location.reload();
      }
    } finally {
      if (isMountedRef.current) setIsLoggingOut(false);
    }
  };

  /**
   * Open CouldBuyFlow pre-filled to edit an area interest, from either the
   * merged Buy interests list or the legacy area-interests deep link.
   * ProfileView is mounted inside the modal stack (ModalOutlet) with no direct
   * prop path to NewLandingPage's CouldBuyFlow, so without a prop handler this
   * falls back to a window event NewLandingPage listens for.
   */
  const handleEditAreaInterest = useCallback(
    (interest: AreaInterest) => {
      closeSubview();
      if (onEditAreaInterest) {
        onEditAreaInterest(interest);
      } else {
        window.dispatchEvent(
          new CustomEvent('jaakko:edit-area-interest', { detail: { interest } }),
        );
      }
    },
    [closeSubview, onEditAreaInterest],
  );

  const renderSubview = () => {
    switch (subview) {
      case ProfileSubview.PriceEstimations:
        return (
          <PriceEstimations
            onBack={closeSubview}
            hideHeader
            estimations={priceEstimations}
            setEstimations={setPriceEstimations}
            initialEstimationId={estimationIdFromUrl}
            onOpenEstimationDetail={openEstimationDetail}
            onCloseEstimationDetail={closeEstimationDetail}
          />
        );
      case ProfileSubview.SellListings:
        return <MyListings listingType="sell" onBack={closeSubview} hideHeader />;
      // AreaInterests is the legacy /profile/area-interests deep link. Areas live
      // in the merged Buy interests list now, so the old URL lands there.
      case ProfileSubview.AreaInterests:
      case ProfileSubview.BuyListings:
        return (
          <MyListings
            listingType="buy"
            onBack={closeSubview}
            hideHeader
            onEditAreaInterest={handleEditAreaInterest}
          />
        );
      case ProfileSubview.NotificationSettings:
        return <NotificationSettings onBack={closeSubview} hideHeader />;
      default:
        return null;
    }
  };

  /**
   * Title the header shows for the open subview, or the profile's own when the
   * root screen is showing. The keys are the ones each subview drew its own
   * header with, so the wording is unchanged.
   */
  const headerTitle = (): string => {
    switch (subview) {
      case ProfileSubview.PriceEstimations:
        return t('priceEstimations.pageTitle');
      case ProfileSubview.SellListings:
        return t('profile.mySellListings');
      case ProfileSubview.AreaInterests:
      case ProfileSubview.BuyListings:
        return t('profile.myBuyInterests');
      case ProfileSubview.NotificationSettings:
        return t('notificationSettings.pageTitle');
      default:
        return headerVariant === 'newLandingPage'
          ? t('newLandingPage.profile.title')
          : t('profile.title');
    }
  };

  const subviewContent = renderSubview();

  return (
    <div className="profile-view__overlay">
      <div className={`profile-view ${subview ? 'profile-view--subview-active' : ''}`}>
        <div className="profile-view__inner">
        <ProfileHeader
          title={headerTitle()}
          variant={headerVariant}
          showBack={!!subview}
          onBack={subview ? closeSubview : onClose}
          onClose={onClose}
        />
        {subviewContent ? (
          <div className="profile-view__subview">
            {subviewContent}
          </div>
        ) : (
          <>
            {/* Avatar + Email */}
            <div className="profile-view__user-info">
              <div className="profile-view__user-info__avatar">
                <ChatUserAvatar
                  name={userEmail || ""}
                  height={80}
                  width={80}
                  verified={isVerified}
                  badgeSize="lg"
                />
              </div>
              {userEmail && (
                <p className="profile-view__user-info__email">{userEmail}</p>
              )}
              {isLoggedIn && !isVerified && (
                <div className="profile-view__verification">
                  {identityVerification.status === 'not_started' && (
                    <button
                      type="button"
                      className="profile-view__verification__cta"
                      onClick={identityVerification.startVerification}
                      disabled={identityVerification.isStarting}
                    >
                      {t('verification.verifyIdentity')}
                    </button>
                  )}
                  {identityVerification.status === 'processing' && (
                    <span className="profile-view__verification__status">
                      {t('verification.inProgress')}
                    </span>
                  )}
                  {(identityVerification.status === 'requires_input'
                    || identityVerification.status === 'failed') && (
                    <button
                      type="button"
                      className="profile-view__verification__cta profile-view__verification__cta--retry"
                      onClick={identityVerification.startVerification}
                      disabled={identityVerification.isStarting}
                    >
                      {t('verification.incompleteTryAgain')}
                    </button>
                  )}
                  {identityVerification.status === 'canceled' && (
                    <button
                      type="button"
                      className="profile-view__verification__cta"
                      onClick={identityVerification.startVerification}
                      disabled={identityVerification.isStarting}
                    >
                      {t('verification.verifyIdentity')}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Action boxes */}
            <div className="profile-view__action-boxes">
              <button className="profile-view__action-box" onClick={() => openSubview(ProfileSubview.SellListings)}>
                <img className="profile-view__action-box__icon" src={houseIcon} alt="" />
                <span className="profile-view__action-box__label">
                  {t('profile.sellListings')}
                </span>
                <ChevronRightIcon size={18} color="#bbb" className="profile-view__action-box__chevron" />
              </button>
              <button className="profile-view__action-box" onClick={() => openSubview(ProfileSubview.BuyListings)}>
                <img className="profile-view__action-box__icon" src={coinIcon} alt="" />
                <span className="profile-view__action-box__label">
                  {t('profile.buyInterests')}
                </span>
                <ChevronRightIcon size={18} color="#bbb" className="profile-view__action-box__chevron" />
              </button>
            </div>

            {/* Price estimations box */}
            <button
              className="profile-view__estimation-box"
              onClick={() => openSubview(ProfileSubview.PriceEstimations)}
            >
              <PriceEstimationIcons />
              <span className="profile-view__estimation-box__label">
                {t('profile.priceEstimations')}
              </span>
              <ChevronRightIcon size={18} color="#bbb" className="profile-view__estimation-box__chevron" />
            </button>

            {/* Phone number section: while the status loads it keeps the add-state layout with its content hidden, so the rows below do not jump */}
            {isLoggedIn && (
              <div
                className={`profile-view__phone-section${phoneStatusLoading ? ' profile-view__phone-section--loading' : ''}`}
                aria-busy={phoneStatusLoading}
              >
                {phoneEditMode === 'idle' && phoneVerified && phoneNumber ? (
                  <>
                    <div className="profile-view__phone-header">
                      <h2 className="profile-view__phone-header__title">
                        {t('profile.phoneNumber')}
                      </h2>
                      <button
                        className="profile-view__phone-header__action"
                        onClick={startEditPhone}
                        disabled={phoneLoading}
                      >
                        {t('profile.edit')}
                      </button>
                    </div>
                    <p className="profile-view__phone-display">{maskPhone(phoneNumber)}</p>
                    <p className="profile-view__phone-description">
                      {t('profile.smsNotificationsDescription')}
                    </p>

                    {showRemoveConfirm ? (
                      <div className="profile-view__phone-remove-confirm">
                        <p className="profile-view__phone-description profile-view__phone-description--danger">
                          {t('profile.removeNumberConfirm')}
                        </p>
                        <div className="profile-view__phone-actions">
                          <button
                            className="profile-view__phone-btn profile-view__phone-btn--danger"
                            onClick={handleRemovePhone}
                            disabled={phoneLoading}
                          >
                            {t('profile.removeNumber')}
                          </button>
                          <button
                            className="profile-view__phone-btn profile-view__phone-btn--secondary"
                            onClick={() => setShowRemoveConfirm(false)}
                            disabled={phoneLoading}
                          >
                            {t('profile.cancel')}
                          </button>
                        </div>
                      </div>
                    ) : null}

                    {phoneError && <p className="profile-view__phone-error">{phoneError}</p>}
                    {phoneSuccess && <p className="profile-view__phone-success">{phoneSuccess}</p>}
                  </>
                ) : phoneEditMode === 'idle' && phoneNumber && !phoneVerified ? (
                  <>
                    <div className="profile-view__phone-header">
                      <h2 className="profile-view__phone-header__title">
                        {t('profile.phoneNumber')}
                      </h2>
                      <button
                        className="profile-view__phone-header__action"
                        onClick={() => {
                          setPhoneEditMode('verify');
                          setPhoneError(null);
                          setPhoneSuccess(null);
                        }}
                        disabled={phoneLoading}
                      >
                        {t('profile.verify')}
                      </button>
                    </div>
                    <p className="profile-view__phone-display">{maskPhone(phoneNumber)}</p>
                    <p className="profile-view__phone-description profile-view__phone-description--warning">
                      {t('profile.verifyCode')}
                    </p>
                    <div className="profile-view__phone-actions">
                      <button
                        className="profile-view__phone-btn profile-view__phone-btn--secondary"
                        onClick={startEditPhone}
                        disabled={phoneLoading}
                      >
                        {t('profile.edit')}
                      </button>
                      <button
                        className="profile-view__phone-btn profile-view__phone-btn--danger"
                        onClick={handleRemovePhone}
                        disabled={phoneLoading}
                      >
                        {t('profile.removeNumber')}
                      </button>
                    </div>
                    {phoneError && <p className="profile-view__phone-error">{phoneError}</p>}
                  </>
                ) : phoneEditMode === 'verify' ? (
                  <>
                    <div className="profile-view__phone-header">
                      <h2 className="profile-view__phone-header__title">
                        {t('profile.phoneNumber')}
                      </h2>
                      <button
                        className="profile-view__phone-header__action"
                        onClick={cancelEditPhone}
                        disabled={phoneLoading}
                      >
                        {t('profile.cancel')}
                      </button>
                    </div>
                    <form
                      className="profile-view__verification"
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (codeInput.length === 6 && !phoneLoading) handleVerifyCode();
                      }}
                    >
                      <p className="profile-view__verification__hint">
                        {t('profile.verifyCode')}
                      </p>
                      <input
                        type="text"
                        className="profile-view__verification__code-input"
                        placeholder={t('profile.verifyCodePlaceholder')}
                        value={codeInput}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setCodeInput(val);
                        }}
                        maxLength={6}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        data-1p-ignore
                        data-lpignore="true"
                        data-form-type="other"
                        disabled={phoneLoading}
                      />
                      <div className="profile-view__verification__actions">
                        <button
                          type="submit"
                          className="profile-view__phone-btn profile-view__phone-btn--primary"
                          disabled={phoneLoading || codeInput.length !== 6}
                        >
                          {t('profile.verify')}
                        </button>
                        <button
                          type="button"
                          className="profile-view__phone-btn profile-view__phone-btn--secondary"
                          onClick={handleResendCode}
                          disabled={phoneLoading}
                        >
                          {t('profile.resendCode')}
                        </button>
                      </div>
                      {phoneError && <p className="profile-view__phone-error">{phoneError}</p>}
                      {phoneSuccess && <p className="profile-view__phone-success">{phoneSuccess}</p>}
                    </form>
                  </>
                ) : phoneEditMode === 'input' ? (
                  <>
                    <div className="profile-view__phone-header">
                      <h2 className="profile-view__phone-header__title">
                        {t('profile.phoneNumber')}
                      </h2>
                      <button
                        className="profile-view__phone-header__action"
                        onClick={cancelEditPhone}
                        disabled={phoneLoading}
                      >
                        {t('profile.cancel')}
                      </button>
                    </div>
                    <div className="profile-view__phone-input-group">
                      <div className={`profile-view__phone-input-wrapper${phoneError ? ' profile-view__phone-input-wrapper--error' : ''}`}>
                        <span className="profile-view__phone-input-prefix">+358</span>
                        <input
                          type="tel"
                          className="profile-view__phone-input"
                          placeholder={t('profile.phonePlaceholder')}
                          value={phoneInput}
                          onChange={(e) => {
                            let val = e.target.value;
                            if (val.startsWith('+358')) val = val.slice(4);
                            if (val.startsWith('358')) val = val.slice(3);
                            val = val.replace(/[^\d\s]/g, '');
                            setPhoneInput(val);
                          }}
                          autoComplete="tel"
                          data-1p-ignore
                          data-lpignore="true"
                          data-form-type="other"
                          disabled={phoneLoading}
                          autoFocus
                        />
                      </div>
                      <div className="profile-view__phone-actions">
                        {!phoneUnchanged && (

                          <button
                            className="profile-view__phone-btn profile-view__phone-btn--primary"
                            onClick={handleAddPhone}
                            disabled={phoneLoading || !phoneInput.trim() || phoneUnchanged}
                          >
                            {t('profile.sendCode')}
                          </button>
                        )}
                        {!showRemoveConfirm && phoneNumber && phoneVerified && (
                          <button
                            className="profile-view__phone-btn profile-view__phone-btn--danger"
                            onClick={() => setShowRemoveConfirm(true)}
                            disabled={phoneLoading}
                          >
                            {t('profile.removeNumber')}
                          </button>
                        )}
                      </div>

                      {showRemoveConfirm && (
                        <div style={{ marginTop: 8 }}>
                          <p className="profile-view__phone-description" style={{ color: '#e74c3c' }}>
                            {t('profile.removeNumberConfirm')}
                          </p>
                          <div className="profile-view__phone-actions">
                            <button
                              className="profile-view__phone-btn profile-view__phone-btn--danger"
                              onClick={handleRemovePhone}
                              disabled={phoneLoading}
                            >
                              {t('profile.removeNumber')}
                            </button>
                            <button
                              className="profile-view__phone-btn profile-view__phone-btn--secondary"
                              onClick={() => setShowRemoveConfirm(false)}
                              disabled={phoneLoading}
                            >
                              {t('profile.cancel')}
                            </button>
                          </div>
                        </div>
                      )}

                      {phoneError && <p className="profile-view__phone-error">{phoneError}</p>}
                      {phoneSuccess && <p className="profile-view__phone-success">{phoneSuccess}</p>}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="profile-view__phone-header">
                      <h2 className="profile-view__phone-header__title">
                        {t('profile.phoneNumber')}
                      </h2>
                      <button
                        className="profile-view__phone-header__action"
                        onClick={startEditPhone}
                        disabled={phoneLoading}
                      >
                        {t('profile.add')}
                      </button>
                    </div>
                    <p className="profile-view__phone-description">
                      {t('profile.addPhoneNumber')}
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Settings section (below separator) */}
            <div className="profile-view__settings-separator" />
            <div className="profile-view__menu">
              <button
                className="profile-view__menu-row"
                onClick={() => openSubview(ProfileSubview.NotificationSettings)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                <span className="profile-view__menu-row__label">
                  {t('profile.notificationSettings')}
                </span>
                <ChevronRightIcon size={18} color="#bbb" className="profile-view__menu-row__chevron" />
              </button>
              <button
                className="profile-view__menu-row profile-view__menu-row--logout"
                onClick={handleLogout}
                disabled={isLoggingOut}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 30 33" fill="none">
                  <path
                    d="M5 28 V5.6
       A1.6 1.6 0 0 1 6.6 4
       H22.4
       A1.6 1.6 0 0 1 24 5.6
       V28"
                    stroke="#555"
                    strokeWidth="2"
                    strokeLinejoin="miter"
                    strokeLinecap="butt"
                  />

                  <rect x="3" y="28.5" width="23" height="3" fill="#555" />

                  <circle cx="20.5" cy="16.5" r="1" fill="#555" />
                </svg>

                <span className="profile-view__menu-row__label">
                  {isLoggingOut ? '...' : t('profile.logOut')}
                </span>
              </button>
            </div>
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default ProfileView;
